import type { ReactNode } from "react";
import { Button } from "@/design-system/components/react";
import type { PushAlerts } from "./usePushAlerts";

/** The header end of the alerts control: Enable alerts (Try again after a failed
 * re-sync), or Alerts on + Turn off. States that need explaining show "Alerts
 * off" here and their hint below the header (`AlertsHint`). */
export function AlertsControl({ alerts }: { alerts: PushAlerts }) {
  const { state, enable, disable } = alerts;
  // Turn off failed: the subscription still exists, so alerts are still on.
  const on = state.kind === "on" || (state.kind === "error" && state.during === "disable");
  if (on)
    return (
      <span className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-muted">
          <span aria-hidden className="size-[7px] rounded-full bg-signal" />
          Alerts on
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-3 text-[12.5px] text-fg-muted"
          onClick={() => void disable()}
        >
          Turn off
        </Button>
      </span>
    );
  switch (state.kind) {
    case "checking":
      return null;
    case "off":
    case "error":
      return (
        <Button size="sm" className="h-8 px-3 text-[12.5px]" onClick={() => void enable()}>
          {/* A failed re-sync retries the same save (enable reuses the subscription). */}
          {state.kind === "error" && state.during === "resync" ? "Try again" : "Enable alerts"}
        </Button>
      );
    case "asking":
      return (
        <Button size="sm" className="h-8 px-3 text-[12.5px]" disabled>
          Enabling…
        </Button>
      );
    default:
      return <span className="whitespace-nowrap text-xs text-fg-muted">Alerts off</span>;
  }
}

function Hint({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="px-4 pt-3">
      <p
        role="status"
        className="grid gap-0.5 rounded-[2px] border border-border-strong bg-surface-2 px-3 py-2.5 text-[12.5px] text-fg-muted"
      >
        <b className="font-semibold text-fg">{title}</b>
        {children}
      </p>
    </div>
  );
}

/** What to do next when alerts can't simply be turned on: a full-width strip
 * under the header (neutral attention cue, no hue). */
export function AlertsHint({ alerts }: { alerts: PushAlerts }) {
  const { state } = alerts;
  switch (state.kind) {
    case "install":
      return (
        <Hint title="Add to Home Screen first">
          In Safari tap Share, then Add to Home Screen. Open a2z Notify from the Home Screen and tap
          Enable alerts.
        </Hint>
      );
    case "unsupported":
      return (
        <Hint title="Alerts are not available in this browser">
          Use Chrome on Android or a computer, or a2z Notify from the Home Screen on an iPhone.
        </Hint>
      );
    case "blocked":
      return (
        <Hint title="Alerts are blocked">
          Allow notifications for a2z Notify in Settings, then reopen the app.
        </Hint>
      );
    case "error":
      // Enable: the server's reason as the title, e.g. "Push is not available"
      // (503). Re-sync and Turn off: a fixed message that already says what to do.
      return state.during === "enable" ? (
        <Hint title={state.message}>Alerts were not changed. Try again later.</Hint>
      ) : (
        <Hint title={state.message} />
      );
    default:
      return null;
  }
}
